import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Mail, FileDown } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const LABELS = { particolari: "Dati relativi alla salute (solo per polizze)", marketing: "Comunicazioni commerciali" };

const Revoke = ({ info, token, onDone }) => {
  const [busy, setBusy] = useState("");
  const revoked = (id) => (info.revocations || []).find((r) => r.consent === id);
  const run = async (id) => {
    if (!window.confirm(`Revocare il consenso: ${LABELS[id]}?`)) return;
    setBusy(id);
    try { await api.post(`/public/privacy/${token}/revoke`, { consent: id }); toast.success("Consenso revocato"); await onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="border-t border-emerald-200 pt-4 space-y-2" data-testid="privacy-revoke-section">
      <p className="text-sm font-medium text-slate-800">Consensi facoltativi</p>
      {Object.keys(LABELS).map((id) => (
        <div key={id} className="flex items-center justify-between gap-3 text-sm">
          <span>{LABELS[id]}: <b>{info.signed_consents?.[id] ? "prestato" : revoked(id) ? `revocato il ${new Date(revoked(id).at).toLocaleDateString("it-IT")}` : "non prestato"}</b></span>
          {info.signed_consents?.[id] && <Button data-testid={`privacy-revoke-${id}`} size="sm" variant="outline" disabled={!!busy} onClick={() => run(id)} className="rounded-full">Revoca</Button>}
        </div>
      ))}
      <p className="text-xs text-slate-500">La revoca non pregiudica la liceità del trattamento svolto prima. Per gli altri diritti (accesso, cancellazione…) scriva al titolare indicato nell'informativa.</p>
    </div>
  );
};

export default function PublicPrivacy() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [consents, setConsents] = useState({});
  const [name, setName] = useState("");
  const [otpSent, setOtpSent] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/public/privacy/${token}`).then((r) => { setInfo(r.data); setName((n) => n || r.data.client_name); })
    .catch((e) => setError(errMsg(e)));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const requiredOk = info?.consents.filter((c) => c.required).every((c) => consents[c.id]);
  const sendOtp = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/public/privacy/${token}/otp`, { consents }); setOtpSent(data.email_masked); toast.success("Codice inviato via email"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const sign = async () => {
    setBusy(true);
    try { await api.post(`/public/privacy/${token}/sign`, { code, consents, full_name: name }); toast.success("Firma registrata, grazie!"); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="privacy-invalid"><p className="text-slate-600">{error}</p></div>;
  if (!info) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;

  return (
    <div className="min-h-screen bg-[#F8FAFC] grain" data-testid="public-privacy-page">
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-5 py-10">
          <p className="text-sky-300 text-xs uppercase tracking-[0.2em] flex items-center gap-2"><ShieldCheck className="h-4 w-4" />Informativa privacy</p>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">Gentile {info.client_name}, legga e firmi l'informativa</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-8 space-y-6">
        <div data-testid="privacy-text" className="bg-white rounded-2xl border border-slate-200 p-6 max-h-[50vh] overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap text-slate-700">{info.text}</div>

        {info.status === "firmata" ? (
          <div data-testid="privacy-signed" className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 space-y-3">
            <p className="font-display font-semibold text-emerald-800">Documento firmato il {new Date(info.signed_at).toLocaleString("it-IT")}</p>
            <a data-testid="privacy-download" href={`${process.env.REACT_APP_BACKEND_URL}/api/public/privacy/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 text-sm font-medium hover:bg-emerald-700 transition-colors"><FileDown className="h-4 w-4" />Scarica la copia firmata (PDF)</a>
            <Revoke info={info} token={token} onDone={load} />
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <h3 className="font-display font-semibold">Consensi</h3>
            {info.consents.map((c) => (
              <label key={c.id} className="flex items-start gap-3 text-sm cursor-pointer" data-testid={`consent-${c.id}`}>
                <Checkbox data-testid={`consent-check-${c.id}`} checked={!!consents[c.id]} onCheckedChange={(v) => setConsents({ ...consents, [c.id]: !!v })} className="mt-0.5" />
                <span>{c.text}{c.required && <span className="text-rose-600"> *</span>}</span>
              </label>
            ))}
            <div className="space-y-1.5">
              <p className="text-xs text-slate-500">Nome e cognome del firmatario</p>
              <Input data-testid="privacy-full-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {!info.has_email && <p className="text-sm text-rose-600">Nessuna email registrata: contatti il consulente per completare la firma.</p>}
            {!otpSent ? (
              <Button data-testid="privacy-send-otp" disabled={!requiredOk || busy || !info.has_email || name.trim().length < 2} onClick={sendOtp} className="w-full h-12 rounded-full bg-[#0F172A] hover:bg-sky-600">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}Ricevi il codice di firma a {info.email_masked}
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">Abbiamo inviato un codice di 6 cifre a <b>{otpSent}</b>. Inseriscilo per firmare.</p>
                <div className="flex gap-2">
                  <Input data-testid="privacy-otp-input" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="000000" className="font-mono text-lg tracking-[0.4em] h-12" />
                  <Button data-testid="privacy-sign-btn" disabled={code.length !== 6 || busy || !requiredOk} onClick={sign} className="h-12 px-6 rounded-full bg-emerald-600 hover:bg-emerald-700">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Firma"}</Button>
                </div>
                <button data-testid="privacy-resend-otp" onClick={sendOtp} disabled={busy} className="text-xs text-sky-700 hover:underline">Non hai ricevuto il codice? Invialo di nuovo</button>
              </div>
            )}
            <p className="text-xs text-slate-400">* consensi necessari. Firma elettronica semplice (eIDAS art. 3 n. 10; CAD art. 20): registriamo data, ora, IP e impronta del documento.</p>
          </div>
        )}
      </main>
    </div>
  );
}
